import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Drawer,
  IconButton,
  Stack,
} from '@mui/material';
import {
  CameraAlt as CaptureIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInFull as OpenFullIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useScreenshots, useCaptureScreenshot, useDeleteScreenshot, useDeviceCommand, screenshotKeys } from '@/hooks/use-screenshots';
import { useDevice } from '@/hooks/use-devices';
import screenshotService from '@/lib/screenshot.service';
import { getAPIErrorMessage } from '@/lib/api';
import { useScreenCaptureStore } from '@/stores/screen-capture.store';
import { useCaptureFlags } from '@/hooks/use-features';
import { useCommandStream, type CommandStateMessage } from '@/hooks/use-command-stream';
import { ConfirmDialog } from './ui/confirm-dialog';
import { ScreenshotGallery } from './screenshot-gallery';
import type { Screenshot, ScreenshotFilter } from '@/types';
import { colors } from '@/theme/tokens';

interface ScreenCaptureProps {
  deviceId: string;
  isOnline: boolean;
}


// captureStateLabel renders the command-lifecycle state into the
// short label + sub-step text shown in the live capture panel,
// matching the Pencil design's "Capturing screen…" + "ACK received ·
// uploading · 12s elapsed" pattern.
function captureStateLabel(cmd: CommandStateMessage | undefined, elapsedMs: number): {
  primary: string;
  secondary: string;
  inFlight: boolean;
} {
  const elapsedLabel = `${Math.max(0, Math.round(elapsedMs / 1000))}s elapsed`;
  if (!cmd) {
    // No command-state data yet — typical for async mode in the first
    // 100ms-5s window before the goroutine backfills command_id or
    // the screenshots list refreshes. Label stays consistent
    // ("Capturing screen…") so the operator doesn't see UX jitter.
    return { primary: 'Capturing screen…', secondary: `publishing to device · ${elapsedLabel}`, inFlight: true };
  }
  switch (cmd.state) {
    case 'queued':
      return { primary: 'Capturing screen…', secondary: `queued · ${elapsedLabel}`, inFlight: true };
    case 'dispatched':
      return { primary: 'Capturing screen…', secondary: `sent to device · ${elapsedLabel}`, inFlight: true };
    case 'executing':
    case 'waiting_user':
      return { primary: 'Capturing screen…', secondary: `ACK received · uploading · ${elapsedLabel}`, inFlight: true };
    case 'succeeded':
      return { primary: 'Capture verified', secondary: `uploaded in ${elapsedLabel}`, inFlight: false };
    case 'failed':
      return { primary: 'Capture failed', secondary: cmd.error_reason || 'device reported failure', inFlight: false };
    case 'timeout':
      return { primary: 'Capture timed out', secondary: cmd.error_reason || 'no device response', inFlight: false };
    case 'cancelled':
      return { primary: 'Capture cancelled', secondary: '', inFlight: false };
    default:
      return { primary: 'Capturing screen…', secondary: elapsedLabel, inFlight: true };
  }
}

export function ScreenCapture({ deviceId, isOnline }: ScreenCaptureProps) {
  const [filter, setFilter] = useState<ScreenshotFilter>({ limit: 8, offset: 0 });
  // Hold the open screenshot by ID rather than by snapshot so the
  // detail panel re-renders against the latest row when the
  // screenshots list refetches (command_id backfill, file_size
  // transition, uploaded_at landing).
  const [detailScreenshotId, setDetailScreenshotId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [captureSnackbar, setCaptureSnackbar] = useState<{
    id: number;
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  // Track the in-flight capture's command_id + start time so the live
  // status panel can show the elapsed timer. Cleared on terminal state
  // or panel dismiss.
  //
  // Backed by a Zustand store keyed by deviceId so the panel resumes
  // when the user navigates from another tab on the device-detail page
  // and back — the local component unmounts but the store survives.
  // A full page reload still drops the tracker (WS reconnects + 90s
  // reaper + 5s polling reconcile the final state).
  const activeCapture = useScreenCaptureStore((s) => s.active[deviceId] ?? null);
  const setActiveCaptureRaw = useScreenCaptureStore((s) => s.setActive);
  const setActiveCapture = useCallback(
    (value: { commandId: string; startedAt: number } | null) => {
      setActiveCaptureRaw(deviceId, value);
    },
    [deviceId, setActiveCaptureRaw],
  );
  const [now, setNow] = useState(() => Date.now());

  const { data, isLoading } = useScreenshots(deviceId, filter);
  const deleteScreenshot = useDeleteScreenshot(deviceId);
  const { wait_for_ack: waitForAck } = useCaptureFlags();
  const { commands, isConnected: wsConnected } = useCommandStream({ deviceId, enabled: !!activeCapture });
  const queryClient = useQueryClient();

  const screenshots = data?.screenshots || [];
  const total = data?.total || 0;

  // Tick a 1Hz counter while a capture is in-flight so the elapsed
  // label updates. Stops as soon as activeCapture clears.
  useEffect(() => {
    if (!activeCapture) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activeCapture]);

  // WS-driven gallery refresh: when any screenshot command in the WS
  // stream reaches a terminal state, invalidate the screenshots query
  // so React Query refetches the list (with the new signed URL +
  // file_size for the just-finished capture). Replaces what the 5s
  // polling fallback in useScreenshots used to do — WS makes it
  // immediate. Polling stays as a safety net for cases where the WS
  // missed an event (reconnect race, network blip).
  //
  // Also invalidates the per-command query (used by the open detail
  // panel's lifecycle timeline) so the timeline transitions update in
  // real time instead of waiting for the 3s poll tick.
  useEffect(() => {
    let invalidateList = false;
    const commandIdsToInvalidate: string[] = [];
    for (const cmd of commands.values()) {
      if (cmd.command_type !== 'screenshot') continue;
      // Any state change for an open command — invalidate so the
      // detail panel's lifecycle re-fetches with state_history.
      commandIdsToInvalidate.push(cmd.command_id);
      if (['succeeded', 'failed', 'timeout'].includes(cmd.state)) {
        invalidateList = true;
      }
    }
    if (invalidateList) {
      queryClient.invalidateQueries({ queryKey: screenshotKeys.lists() });
      // Counts query is keyed under screenshotKeys.all → 'counts' → deviceId.
      // Invalidate the broad prefix so terminal-state transitions
      // (which change verified/in_progress/failed buckets) keep the
      // chip badges in sync.
      queryClient.invalidateQueries({ queryKey: [...screenshotKeys.all, 'counts'] });
    }
    for (const cid of commandIdsToInvalidate) {
      queryClient.invalidateQueries({ queryKey: ['device-command', cid] });
    }
  }, [commands, queryClient]);

  // When the WS sees the active command land in a terminal state, leave
  // the panel up for ~2s as confirmation, then auto-dismiss.
  useEffect(() => {
    if (!activeCapture || !activeCapture.commandId) return;
    const cmd = commands.get(activeCapture.commandId);
    if (!cmd) return;
    const terminal = ['succeeded', 'failed', 'timeout', 'cancelled'].includes(cmd.state);
    if (!terminal) return;
    const t = setTimeout(() => setActiveCapture(null), 2000);
    return () => clearTimeout(t);
  }, [activeCapture, commands]);

  // Async mode link-up: the screenshot row arrives via list refresh
  // before the command_id is known to the FE (the backend goroutine
  // backfills command_id after dispatch returns). When a new screenshot
  // appears (captured_at >= startedAt) with a command_id, adopt it so
  // the live panel's WS-driven state takes over from the elapsed-only
  // fallback. Also dismiss the panel once the row reaches a terminal
  // file_size state, since the gallery tile now carries the result.
  useEffect(() => {
    if (!activeCapture) return;
    const sinceStart = screenshots.find((s) => {
      const t = new Date(s.captured_at).getTime();
      return !Number.isNaN(t) && t >= activeCapture.startedAt - 1000;
    });
    if (!sinceStart) return;
    if (sinceStart.command_id && sinceStart.command_id !== activeCapture.commandId) {
      setActiveCapture({ commandId: sinceStart.command_id, startedAt: activeCapture.startedAt });
      return;
    }
    if (sinceStart.file_size !== 0) {
      const t = setTimeout(() => setActiveCapture(null), 2000);
      return () => clearTimeout(t);
    }
  }, [activeCapture, screenshots]);

  // Hard ceiling: if nothing else dismisses the panel within 90s,
  // drop it. Matches the registry Timeout for the screenshot command.
  useEffect(() => {
    if (!activeCapture) return;
    const t = setTimeout(() => setActiveCapture(null), 90_000);
    return () => clearTimeout(t);
  }, [activeCapture]);

  const showCaptureSnackbar = useCallback((severity: 'success' | 'error', message: string) => {
    setCaptureSnackbar({ id: Date.now(), severity, message });
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback(() => setSnackbarOpen(false), []);
  const handleSnackbarExited = useCallback(() => setCaptureSnackbar(null), []);

  const capture = useCaptureScreenshot(deviceId);

  const handleCapture = useCallback(() => {
    if (capture.isPending) return;
    const startedAt = Date.now();
    // Open the live panel immediately on click. command_id is filled in
    // when the mutation returns (sync mode) or remains null while the
    // async goroutine backfills it. The panel renders "Sending
    // capture…" with the elapsed timer in the meantime; WS-driven
    // state transitions take over once command_id is known.
    setActiveCapture({ commandId: '', startedAt });
    capture.mutate(undefined, {
      onSuccess: (resp) => {
        if (resp.command_id) {
          setActiveCapture({ commandId: resp.command_id, startedAt });
        }
        showCaptureSnackbar(
          'success',
          waitForAck ? 'Screenshot captured.' : 'Capture command sent. Screenshot will appear shortly.',
        );
      },
      onError: (err) => {
        setActiveCapture(null);
        const axiosErr = err as { response?: { status?: number } };
        const status = axiosErr?.response?.status;
        const serverMessage = getAPIErrorMessage(err, '');
        let message = 'Capture failed.';
        if (status === 504) message = 'Capture failed: device did not confirm upload.';
        else if (status === 502) message = 'Capture failed: device reported an error.';
        else if (status === 409) message = 'Device must be online to capture screenshots.';
        else if (serverMessage) message = `Capture failed: ${serverMessage}`;
        showCaptureSnackbar('error', message);
      },
    });
  }, [capture, showCaptureSnackbar, waitForAck]);

  const handleView = useCallback((screenshot: Screenshot) => {
    setDetailScreenshotId(screenshot.id);
  }, []);

  // Resolve the open screenshot from the latest list so the panel
  // re-renders against fresh data when the list refetches. When the
  // row disappears from the current page (pagination, filter change,
  // delete), keep showing the LAST-KNOWN snapshot so the drawer body
  // doesn't render empty under the still-open drawer chrome.
  const lastKnownDetailRef = useRef<Screenshot | null>(null);
  const fromList = detailScreenshotId
    ? screenshots.find((s) => s.id === detailScreenshotId) ?? null
    : null;
  if (fromList) {
    lastKnownDetailRef.current = fromList;
  } else if (!detailScreenshotId) {
    lastKnownDetailRef.current = null;
  }
  const detailScreenshot = fromList ?? lastKnownDetailRef.current;

  const handleDelete = useCallback((id: string) => setConfirmDeleteId(id), []);
  const handleCancelDelete = useCallback(() => setConfirmDeleteId(null), []);

  const handleConfirmDelete = useCallback(() => {
    const id = confirmDeleteId;
    if (!id) return;
    // Optimistic close: drop the drawer + confirm dialog BEFORE the
    // mutation resolves so there's no blank-drawer flicker during the
    // close animation. The mutation's onMutate already does the
    // optimistic list removal — if the server rejects, useScreenshots
    // rolls back and the row reappears in the gallery; the operator
    // can re-open it.
    if (detailScreenshotId === id) setDetailScreenshotId(null);
    setConfirmDeleteId(null);
    deleteScreenshot.mutate(id);
  }, [confirmDeleteId, deleteScreenshot, detailScreenshotId]);

  // Resolve the active command state for the live panel (if any).
  const activeCommand = activeCapture ? commands.get(activeCapture.commandId) : undefined;
  const captureLabel = activeCapture
    ? captureStateLabel(activeCommand, now - activeCapture.startedAt)
    : null;

  return (
    <Box>
      {/* Header — title + subtitle on the left, dark Capture-now CTA on
          the right. Mirrors the Pencil KzuzC layout. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: captureLabel ? 1.5 : 2.5,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 0.5 }}>
            Screen capture
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            {total > 0 ? `${total} ${total === 1 ? 'capture' : 'captures'}` : 'No captures yet'}
          </Typography>
        </Box>
        <Tooltip
          title={
            !isOnline
              ? 'Device is offline'
              : activeCapture
                ? 'A capture is already in progress'
                : ''
          }
        >
          <span style={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={
                capture.isPending || activeCapture
                  ? <CircularProgress size={16} color="inherit" />
                  : <CaptureIcon fontSize="small" />
              }
              onClick={handleCapture}
              // Block concurrent captures: the UI tracks one
              // activeCapture at a time, and the design's live panel
              // only renders one in-flight capture. Operator clicking
              // multiple times in async mode would orphan earlier
              // captures from the UI while the backend kept publishing
              // — confusing. Re-enabled after panel auto-dismiss
              // (terminal state) or manual Cancel.
              disabled={!isOnline || capture.isPending || activeCapture !== null}
              // Uses the app's primary action color (orange) to match
              // other CTAs throughout the device-detail screen instead
              // of the standalone black from the early design mock.
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 1.5,
                py: 1.1,
                px: 2.25,
              }}
            >
              {capture.isPending || activeCapture ? 'Capturing…' : 'Capture now'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Live capture status panel — only visible while a capture is
          in-flight. Powered by useCommandStream when the backend
          returned a command_id; falls back to elapsed-only text
          otherwise. */}
      {captureLabel && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            bgcolor: '#F9FAFB',
            border: '1px solid',
            borderColor: '#F3F4F6',
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: captureLabel.inFlight ? colors.primary.main : '#00BC3C',
              animation: captureLabel.inFlight ? 'pulse 1.6s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.35 },
              },
            }}
          />
          <Typography sx={{ color: '#111', fontWeight: 500, fontSize: 13 }}>{captureLabel.primary}</Typography>
          {captureLabel.secondary && (
            <Typography sx={{ color: '#6B7280', fontSize: 12 }}>{captureLabel.secondary}</Typography>
          )}
          {/* WS-dead indicator: if the live stream has been disconnected
              for more than 5s while the capture is still in-flight,
              warn the operator so they don't trust the elapsed-only
              fallback. Polling fallback in useScreenshots still catches
              the terminal state, just slower. */}
          {captureLabel.inFlight && !wsConnected && now - (activeCapture?.startedAt ?? now) > 5000 && (
            <Typography sx={{ color: '#E60A32', fontSize: 11, fontStyle: 'italic' }}>
              · live updates offline
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {captureLabel.inFlight && (
            <Tooltip
              arrow
              placement="top"
              title="Hides this banner. The device keeps capturing — there's no backend cancel yet."
            >
              <Button
                variant="text"
                size="small"
                onClick={() => setActiveCapture(null)}
                sx={{ color: '#6B7280', fontSize: 12, textTransform: 'none', minWidth: 'auto', fontWeight: 500 }}
              >
                Dismiss
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Gallery */}
      <ScreenshotGallery
        screenshots={screenshots}
        total={total}
        isLoading={isLoading}
        filter={filter}
        onFilterChange={setFilter}
        onView={handleView}
        onDelete={handleDelete}
        isDeleting={deleteScreenshot.isPending}
      />

      {/* Detail panel — slides in from the right per the Pencil design.
          Replaces the modal ScreenshotViewer dialog for single-shot
          inspection. Compare-mode is intentionally not wired in this
          redesign (handleCompare is a no-op). */}
      <Drawer
        anchor="right"
        open={detailScreenshotId !== null}
        onClose={() => setDetailScreenshotId(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, bgcolor: '#FFFFFF' } }}
      >
        {detailScreenshot && (
          <ScreenshotDetailPanel
            deviceId={deviceId}
            screenshot={detailScreenshot}
            onClose={() => setDetailScreenshotId(null)}
            onDelete={() => handleDelete(detailScreenshot.id)}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete screenshot?"
        icon={<DeleteIcon sx={{ color: colors.primary.main }} />}
        description="This will permanently remove the screenshot from storage. This action cannot be undone."
        confirmLabel="Delete"
        loadingLabel="Deleting…"
        destructive
        loading={deleteScreenshot.isPending}
        error={deleteScreenshot.isError ? 'Failed to delete. Please try again.' : undefined}
      />

      {captureSnackbar && (
        <Snackbar
          key={captureSnackbar.id}
          open={snackbarOpen}
          autoHideDuration={
            // Scale error duration with message length so verbose
            // device-error messages aren't auto-dismissed before the
            // operator finishes reading. ~50ms/char + 6s floor.
            captureSnackbar.severity === 'error'
              ? Math.min(15000, Math.max(6000, captureSnackbar.message.length * 50))
              : 4000
          }
          onClose={handleSnackbarClose}
          TransitionProps={{ onExited: handleSnackbarExited }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity={captureSnackbar.severity} variant="filled" onClose={handleSnackbarClose} sx={{ width: '100%' }}>
            {captureSnackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}

// ScreenshotDetailPanel renders the right-side drawer body matching the
// updated design: header (with screenshot_id + command_id subtitle),
// preview, lifecycle timeline (state_history rendered as a checkpoint
// list), expanded metadata, and a wider primary action.
function ScreenshotDetailPanel({
  deviceId,
  screenshot,
  onClose,
  onDelete,
}: {
  deviceId: string;
  screenshot: Screenshot;
  onClose: () => void;
  onDelete: () => void;
}) {
  const { data: command } = useDeviceCommand(screenshot.command_id || null);
  const { data: device } = useDevice(deviceId);
  // Move keyboard focus into the drawer on open so tab/arrow navigation
  // and screen-readers move into the panel content instead of staying
  // on the gallery grid. Focuses the close button as the most-obvious
  // landmark + escape-route. Re-runs each time a different screenshot
  // is opened.
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [screenshot.id]);

  // Render in the most natural unit (B / KB / MB) — earlier hardcoded
  // MB rounded sub-megabyte files to "0.0 MB".
  const sizeLabel = screenshot.file_size > 0 ? fmtSize(screenshot.file_size) : null;
  // Space-separated thousands, matching the design's "4 808 273 bytes".
  const sizeBytes = screenshot.file_size > 0
    ? (() => {
        const s = String(screenshot.file_size);
        const parts: string[] = [];
        for (let i = s.length; i > 0; i -= 3) parts.unshift(s.slice(Math.max(0, i - 3), i));
        return parts.join(' ');
      })()
    : null;
  const uploadedAt = screenshot.uploaded_at ? new Date(screenshot.uploaded_at) : null;
  const capturedAt = new Date(screenshot.captured_at);
  const storageKey = screenshot.storage_key ?? '';
  const objectName = storageKey.split('/').pop() || storageKey || '(no object yet)';
  const objectShort =
    objectName.length > 18 ? `${objectName.slice(0, 4)}…${objectName.slice(-12)}` : objectName;

  // Build the lifecycle timeline from the command's state_history +
  // screenshot's file_size verification. Each checkpoint has:
  //  - status: done / pending / failed
  //  - label + sublabel
  //  - relative time (+X.Xs from t0)
  const lifecycle = buildLifecycleCheckpoints(screenshot, command, device?.agent_version);

  const shortID = (id: string | undefined | null, len = 6) =>
    !id ? '' : id.replace(/-/g, '').slice(0, len);
  const screenshotShort = shortID(screenshot.id);
  const commandShort = shortID(screenshot.command_id);

  const copyToClipboard = (text: string) => {
    if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      {/* Header — title + subtitle showing scr_xxxxxx · cmd_xxxxxx,
          matching the updated design (Screenshot detail). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 2.5 },
          py: 2,
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#111' }}>Screenshot detail</Typography>
          <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.25, fontFamily: 'monospace' }}>
            scr_{screenshotShort}
            {commandShort && ` · cmd_${commandShort}`}
          </Typography>
        </Box>
        <IconButton
          ref={closeButtonRef}
          size="small"
          onClick={onClose}
          aria-label="Close detail panel"
          sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: '#F4F4F4' }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Preview */}
      <Box
        sx={{
          bgcolor: '#1A1A2E',
          height: 280,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {screenshot.download_url && screenshot.file_size > 0 ? (
          <Box
            component="img"
            src={screenshot.download_url}
            alt="Screenshot preview"
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <ImageIcon sx={{ color: '#3F3F5C', fontSize: 64 }} />
        )}
      </Box>

      {/* Lifecycle timeline. Hidden when the screenshot has no linked
          command (historical rows, async captures before backfill). */}
      {lifecycle.length > 0 && (
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', letterSpacing: 0.6 }}>
              LIFECYCLE
            </Typography>
            <Tooltip
              arrow
              placement="right"
              title={
                <Box sx={{ p: 0.5, fontSize: 12, lineHeight: 1.5, maxWidth: 280 }}>
                  <Box sx={{ mb: 0.5 }}>
                    First row is absolute time (t₀ = queued). Other rows are{' '}
                    <b>cumulative</b> offsets from t₀.
                  </Box>
                  <Box sx={{ color: '#9CA3AF', fontSize: 11 }}>
                    "Device uploaded to GCS" uses the GCS object's <code>Updated</code>{' '}
                    metadata — i.e. when the device's PUT landed at GCS.
                  </Box>
                </Box>
              }
            >
              <HelpIcon
                sx={{
                  fontSize: 14,
                  color: '#9CA3AF',
                  cursor: 'help',
                  '&:hover': { color: '#6B7280' },
                }}
              />
            </Tooltip>
          </Box>
          <Stack spacing={1.75}>
            {lifecycle.map((cp, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <Box sx={{ pt: 0.25 }}>
                  {cp.status === 'done' && <CheckCircleIcon sx={{ fontSize: 18, color: '#00BC3C' }} />}
                  {cp.status === 'pending' && <PendingIcon sx={{ fontSize: 18, color: '#D1D5DB' }} />}
                  {cp.status === 'failed' && <ErrorIcon sx={{ fontSize: 18, color: '#E60A32' }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: cp.status === 'pending' ? '#9CA3AF' : '#111' }}>
                      {cp.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {cp.relative}
                    </Typography>
                  </Box>
                  {cp.sublabel && (
                    <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.25 }}>{cp.sublabel}</Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Metadata */}
      <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2, borderTop: '1px solid #F3F4F6' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', letterSpacing: 0.6, mb: 1.5 }}>
          METADATA
        </Typography>
        <Stack spacing={1.25}>
          <MetaRow k="Sent command at" v={formatUTC(capturedAt)} />
          <MetaRow k="Uploaded at" v={uploadedAt ? formatUTC(uploadedAt) : '—'} />
          <MetaRow k="Initiator" v={command?.initiator?.user_id || '—'} />
          {sizeLabel && <MetaRow k="File size" v={sizeLabel} />}
          {/* Signed URLs are minted with a 1h TTL in screenshot.service.ts
              (`time.Hour` on the backend). Without a precise generated-at
              timestamp from the API, we approximate from the response's
              freshness — the URL is at most 1 hour old. */}
          {screenshot.download_url && (
            <MetaRow k="Signed URL" v="expires within 1h" />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ color: '#9CA3AF', fontSize: 12 }}>GCS object</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ color: '#111', fontSize: 12, fontWeight: 500, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {objectShort}
              </Typography>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(storageKey)}
                sx={{ width: 22, height: 22, color: '#9CA3AF', '&:hover': { color: '#111', bgcolor: '#F4F4F4' } }}
                title="Copy full storage key"
              >
                <CopyIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          </Box>
          {sizeBytes && <MetaRow k="Bytes" v={sizeBytes} />}
        </Stack>
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Action row — wider primary action per the updated design */}
      <Box sx={{ display: 'flex', gap: 1.25, p: 2.5, borderTop: '1px solid #F3F4F6' }}>
        <Button
          variant="contained"
          startIcon={<OpenFullIcon fontSize="small" />}
          disabled={!screenshot.download_url || screenshot.file_size <= 0}
          onClick={() => {
            if (screenshot.download_url) window.open(screenshot.download_url, '_blank', 'noopener,noreferrer');
          }}
          sx={{
            flex: 1,
            bgcolor: colors.primary.main,
            color: '#FFF',
            textTransform: 'none',
            fontWeight: 500,
            py: 1.1,
            '&:hover': { bgcolor: colors.primary.active },
          }}
        >
          Open full size
        </Button>
        <IconButton
          disabled={screenshot.file_size <= 0}
          onClick={async () => {
            // Fetch a freshly-signed attachment-URL via the
            // authenticated apiClient (a direct browser GET wouldn't
            // include the Bearer token). The returned URL carries
            // Content-Disposition: attachment, so a synthetic <a
            // click triggers a real download cross-origin instead of
            // opening the image inline.
            try {
              const { url, filename } = await screenshotService.getDownloadURL(deviceId, screenshot.id);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              a.rel = 'noopener noreferrer';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            } catch (err) {
              console.error('[ScreenCapture] Failed to start download:', err);
            }
          }}
          sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#F4F4F4', color: '#374151' }}
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={onDelete}
          sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#F4F4F4', color: '#E60A32' }}
        >
          <DeleteIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

// Lifecycle checkpoint shown in the timeline.
interface LifecycleCheckpoint {
  status: 'done' | 'pending' | 'failed';
  label: string;
  sublabel?: string;
  relative: string;
}

// buildLifecycleCheckpoints maps device_commands.state_history + the
// screenshot row's verification state to the 4-step timeline shown in
// the design: MQTT command published → Device ACK → Uploaded to GCS →
// HEAD verified. The first checkpoint renders the absolute timestamp;
// subsequent checkpoints render `+X.Xs` deltas relative to the first.
//
// When `command` is undefined (in-progress capture whose command_id
// hasn't been backfilled yet), the timeline still renders with all
// checkpoints in the pending state — driven by the screenshot row
// alone — so the operator sees the structure of what's coming even
// before the WS/poll catches up.
function buildLifecycleCheckpoints(
  screenshot: Screenshot,
  command: ReturnType<typeof useDeviceCommand>['data'],
  agentVersion?: string,
): LifecycleCheckpoint[] {
  // Without a command, build a pending skeleton off the screenshot
  // row's captured_at. The first checkpoint is anchored at the
  // capture-request timestamp; subsequent steps remain pending until
  // the command row materializes.
  if (!command) {
    const capturedAtAbs = absoluteHMS(screenshot.captured_at);
    return [
      {
        status: screenshot.file_size < 0 ? 'failed' : 'pending',
        label: 'MQTT command published',
        sublabel: 'awaiting command record…',
        relative: capturedAtAbs || '—',
      },
      {
        status: 'pending',
        label: 'Device processing',
        sublabel: 'receive · capture · upload…',
        relative: '—',
      },
      {
        status: 'pending',
        label: 'Device ACK received',
        relative: '—',
      },
      {
        status: 'pending',
        label: 'Server verified · file_size > 0',
        relative: '—',
      },
    ];
  }

  const transitions = new Map<string, { at: string; detail?: string }>();
  for (const t of command.state_history ?? []) {
    transitions.set(t.state, { at: t.at, detail: t.detail });
  }

  // t0 = the earliest transition (queued).
  const firstT = command.state_history?.[0]?.at;
  const t0 = firstT ? new Date(firstT).getTime() : new Date(command.created_at).getTime();
  const relFrom = (at: string | undefined): string => {
    if (!at) return '—';
    const dt = new Date(at).getTime() - t0;
    if (dt < 0 || !Number.isFinite(dt)) return '—';
    if (dt < 100) return '+0s';
    if (dt < 1000) return `+${dt}ms`;
    return `+${(dt / 1000).toFixed(1)}s`;
  };
  // Space-separated thousands, matching the design's "4 808 273 bytes".
  // Intl with locales like 'fr-FR' uses NBSP which doesn't render
  // identically; do it manually so it's a regular space.
  const fmtBytes = (n: number): string => {
    const s = String(n);
    const parts: string[] = [];
    for (let i = s.length; i > 0; i -= 3) parts.unshift(s.slice(Math.max(0, i - 3), i));
    return parts.join(' ');
  };

  const storageKey = screenshot.storage_key ?? '';
  const objectName = storageKey.split('/').pop() || '';
  const objectShort =
    objectName.length > 18 ? `${objectName.slice(0, 4)}…${objectName.slice(-12)}` : objectName || '—';

  const checkpoints: LifecycleCheckpoint[] = [];

  // 1. MQTT command published — at dispatched. First step shows the
  //    absolute timestamp; later steps show relative deltas. The full
  //    topic is stamped into the dispatched-state detail by the
  //    backend (see dispatch.go), so the FE just renders it.
  const dispatched = transitions.get('dispatched');
  let topicSub: string | undefined;
  if (dispatched?.detail) {
    topicSub = dispatched.detail; // already prefixed "topic: aviron/..."
  } else if (command.request_id) {
    topicSub = `request_id ${command.request_id.slice(0, 8)}…`;
  }
  checkpoints.push({
    status: dispatched ? 'done' : 'pending',
    label: 'MQTT command published',
    sublabel: topicSub,
    relative: dispatched ? absoluteHMS(dispatched.at) : '—',
  });

  // Chronological order matches the actual flow:
  //   server publishes → device receives + caps + PUTs to GCS
  //   → device sends ACK back → server runs GCS HEAD.
  //
  // We have explicit timestamps for:
  //   - dispatched (server publish)
  //   - uploaded_at (GCS Object.Updated, set when device's PUT lands)
  //   - succeeded (device's ACK reaches server)
  //   - HEAD verified ~= succeeded (we HEAD inline after ACK).
  //
  // The "device received & captured" microsteps happen between
  // dispatched and uploaded_at; we don't have separate timestamps for
  // those without device-side telemetry, so they're folded into the
  // "Device uploaded to GCS" checkpoint's sublabel.
  const succeeded = transitions.get('succeeded');
  const failed = transitions.get('failed');
  const timedOut = transitions.get('timeout');
  const ackPrefix = agentVersion ? `agent v${agentVersion} · ` : '';

  // 2. Device uploaded to GCS — uploaded_at = the GCS object's
  //    Updated metadata, which is when the device's PUT request
  //    completed (so this covers device receive + cap + upload).
  if (screenshot.file_size > 0 && screenshot.uploaded_at) {
    checkpoints.push({
      status: 'done',
      label: 'Device uploaded to GCS',
      sublabel: `${fmtSize(screenshot.file_size)} · object ${objectShort}`,
      relative: relFrom(screenshot.uploaded_at || undefined),
    });
  } else if (screenshot.file_size < 0) {
    checkpoints.push({
      status: 'failed',
      label: 'Upload failed',
      sublabel: 'device reported result:false',
      relative: '—',
    });
  } else {
    checkpoints.push({
      status: 'pending',
      label: 'Device processing',
      sublabel: 'receive · capture · upload…',
      relative: '—',
    });
  }

  // 3. Device sent ACK to server — succeeded state.
  if (succeeded) {
    checkpoints.push({
      status: 'done',
      label: 'Device ACK received',
      sublabel: `${ackPrefix}uploadScreen accepted`,
      relative: relFrom(succeeded.at),
    });
  } else if (failed) {
    checkpoints.push({
      status: 'failed',
      label: 'Device reported failure',
      sublabel: command.error_reason || failed.detail,
      relative: relFrom(failed.at),
    });
  } else if (timedOut) {
    checkpoints.push({
      status: 'failed',
      label: 'No device response',
      sublabel: command.error_reason || 'reaper transition',
      relative: relFrom(timedOut.at),
    });
  } else {
    checkpoints.push({
      status: 'pending',
      label: 'Awaiting device ACK',
      relative: '—',
    });
  }

  // 4. Server verified file_size via GCS HEAD — runs inline after ACK,
  //    so the timestamp ~= succeeded_at. Byte count uses space
  //    separator per design.
  if (screenshot.file_size > 0) {
    checkpoints.push({
      status: 'done',
      label: 'Server verified · file_size > 0',
      sublabel: `${fmtBytes(screenshot.file_size)} bytes`,
      relative: relFrom(succeeded?.at),
    });
  }

  // Monotonic reconciliation: the checkpoints are built from two independent
  // signals — the command's state_history (steps 1 & 3) and the screenshot's
  // GCS fields (steps 2 & 4) — which can populate out of order (e.g. the async
  // re-HEAD path sets file_size with no recorded ACK). But the evidence is
  // transitive: a server-verified file in GCS can't exist unless the command
  // was published and the device uploaded. So promote any earlier `pending`
  // step to `done` once a later step is `done`, so the timeline never shows a
  // completed step above a still-pending earlier one. `failed` is never
  // promoted — a real failure stays visible.
  let laterDone = false;
  for (let i = checkpoints.length - 1; i >= 0; i--) {
    if (checkpoints[i].status === 'done') {
      laterDone = true;
    } else if (laterDone && checkpoints[i].status === 'pending') {
      checkpoints[i].status = 'done';
    }
  }

  return checkpoints;
}

// fmtSize renders bytes in the most natural unit: bytes < 1KB, KB up
// to 1MB, then MB. Avoids the "45 KB shown as 0.0 MB" bug from
// toFixed(1) rounding sub-MB values to zero.
function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function MetaRow({ k, v }: { k: string; v: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ color: '#9CA3AF', fontSize: 12 }}>{k}</Typography>
      <Typography sx={{ color: '#111', fontSize: 12, fontWeight: 500, textAlign: 'right' }}>{v}</Typography>
    </Box>
  );
}

function formatUTC(date: Date): string {
  // Human-readable local time: "May 26, 2026, 2:33:33 PM"
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

// absoluteHMS renders "HH:MM:SS.mmm" — used by the lifecycle
// timeline's first checkpoint to anchor relative offsets.
function absoluteHMS(at: string | undefined): string {
  if (!at) return '—';
  const d = new Date(at);
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mi}:${ss}.${ms}`;
}
